import React, { JSX, useEffect, useState } from 'react';
import { MapConfig } from './MapsInterfaces';
import Keys from '../../Translations/generated/park-map.en.json.keys';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarked } from '@fortawesome/free-solid-svg-icons';
import { ApiImageContainer } from '@ibe/api';
import MapModal from './MapModal';

type HistoryMethod = (data: unknown, unused: string, url?: string | URL | null) => void;

const ParkMap = ({
  mapConfig,
  mapStaticImage,
  handleHotelClick,
  displayMode,
  mapButtonLabel
}: {
  mapConfig: MapConfig;
  mapStaticImage?: ApiImageContainer;
  handleHotelClick?: (hotelId: string) => Promise<void>;
  displayMode: 'IMAGE' | 'BUTTON';
  mapButtonLabel?: string;
}): JSX.Element => {
  const { t } = useTranslation('park-map');

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (!isModalOpen) return;

    const originalReplaceState = globalThis.history.replaceState;
    const originalPushState = globalThis.history.pushState;

    const blockRogueParams = (method: HistoryMethod) =>
      function (this: History, state: unknown, unused: string, url?: string | URL | null) {
        if (url && typeof url === 'string' && url.includes('fromOnPopstate')) {
          const cleanUrl = url
            .replace('&fromOnPopstate=true', '')
            .replace('?fromOnPopstate=true', '');
          return method.call(this, state, unused, cleanUrl);
        }
        return method.call(this, state, unused, url);
      };

    globalThis.history.replaceState = blockRogueParams(originalReplaceState);
    globalThis.history.pushState = blockRogueParams(originalPushState);

    const handlePopState = (e: PopStateEvent) => {
      e.stopImmediatePropagation();
      setIsModalOpen(false);
    };

    globalThis.addEventListener('popstate', handlePopState, { capture: true });

    return () => {
      globalThis.history.replaceState = originalReplaceState;
      globalThis.history.pushState = originalPushState;
      globalThis.removeEventListener('popstate', handlePopState, { capture: true });
    };
  }, [isModalOpen]);

  const handleOpenModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (isModalOpen) return;

    const currentState = globalThis.history.state || {};
    globalThis.history.pushState(
      { ...currentState, mapModalOpen: true },
      '',
      globalThis.location.href
    );
    setIsModalOpen(true);
  };

  const handleCloseModal = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!isModalOpen) return;

    if (globalThis.history.state?.mapModalOpen) {
      globalThis.history.back();
    } else {
      setIsModalOpen(false);
    }
  };

  return (
    <>
      <MapModal
        isModalOpen={isModalOpen}
        handleCloseModal={handleCloseModal}
        mapConfig={mapConfig}
        handleHotelClick={handleHotelClick}
      />

      {!isModalOpen && (
        <>
          {displayMode === 'IMAGE' && (
            <button className={'map-preview-wrapper'} onClick={handleOpenModal}>
              {mapStaticImage?.small && (
                <img src={mapStaticImage?.small.url} alt={mapStaticImage?.small.altText} />
              )}
              <div className={'map-preview-banner'}>
                <span>{t(Keys.bannerText)}</span>
              </div>
            </button>
          )}
          {displayMode === 'BUTTON' && (
            <button className={'map-button'} onClick={handleOpenModal}>
              <FontAwesomeIcon icon={faMapMarked} />
              {mapButtonLabel || t(Keys.MAP)}
            </button>
          )}
        </>
      )}
    </>
  );
};

export default ParkMap;
